import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { OrderDetail, OrderShipment } from "@oms/api-client";
import {
  Avatar,
  Badge,
  type BadgeProps,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Skeleton,
} from "@oms/ui";
import { ArrowLeft, ExternalLink, Package, Truck } from "lucide-react";
import { PageShell } from "@/components/pages/page-shell";
import { ChannelBadge } from "@/components/orders/channel-badge";
import { OrderStatusBadge } from "@/components/orders/order-status-badge";
import { ordersApi } from "@/lib/api";
import { orderQueryKeys, ordersLiveQueryOptions } from "@/lib/orders";
import { DEFAULT_ORDERS_SEARCH } from "@/lib/orders-search";
import {
  formatCurrency,
  formatDateTime,
  formatExternalId,
  humanizeStatus,
} from "@/lib/order-format";

export const Route = createFileRoute("/app/orders_/$orderId")({
  component: OrderDetailPage,
});

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const paymentVariant: Record<string, BadgeVariant> = {
  paid: "confirmed",
  pending: "pending",
  authorized: "pending",
  partially_paid: "pending",
  partially_refunded: "neutral",
  refunded: "neutral",
  voided: "neutral",
  expired: "cancelled",
};

const fulfillmentVariant: Record<string, BadgeVariant> = {
  fulfilled: "confirmed",
  unfulfilled: "pending",
  partially_fulfilled: "pending",
  in_progress: "processing",
  on_hold: "pending",
};

const shipmentVariant: Record<OrderShipment["status"], BadgeVariant> = {
  pending: "neutral",
  in_transit: "processing",
  delivered: "delivered",
  failed: "cancelled",
  returned: "neutral",
};

const sourceLabels: Record<string, string> = {
  web: "Online Store",
  pos: "Point of Sale",
  shopify_draft_order: "Draft order",
  iphone: "Shopify mobile",
  android: "Shopify mobile",
};

function OrderDetailPage() {
  const { orderId } = Route.useParams();

  const orderQuery = useQuery({
    queryKey: orderQueryKeys.detail(orderId),
    queryFn: async () => {
      const response = await ordersApi.get(orderId);
      if (!response.data) {
        throw new Error("Order not found");
      }
      return response.data;
    },
    ...ordersLiveQueryOptions,
  });

  return (
    <PageShell className="space-y-6">
      <Link
        to="/app/orders"
        search={DEFAULT_ORDERS_SEARCH}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Orders
      </Link>

      {orderQuery.isLoading ? <OrderDetailSkeleton /> : null}

      {orderQuery.isError ? (
        <ErrorState
          description="Could not load this order."
          onRetry={() => void orderQuery.refetch()}
        />
      ) : null}

      {orderQuery.data ? <OrderDetailView order={orderQuery.data} /> : null}
    </PageShell>
  );
}

function OrderDetailView({ order }: { order: OrderDetail }) {
  const source = order.sourceName
    ? (sourceLabels[order.sourceName] ?? humanizeStatus(order.sourceName))
    : "";

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-2xl font-semibold tracking-tight text-text">
            {formatExternalId(order.externalId)}
          </h1>
          {order.financialStatus ? (
            <Badge variant={paymentVariant[order.financialStatus] ?? "neutral"} dot>
              {humanizeStatus(order.financialStatus)}
            </Badge>
          ) : null}
          {order.fulfillmentStatus ? (
            <Badge variant={fulfillmentVariant[order.fulfillmentStatus] ?? "neutral"} dot>
              {humanizeStatus(order.fulfillmentStatus)}
            </Badge>
          ) : null}
          <OrderStatusBadge status={order.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-text-muted">
          <span>
            {formatDateTime(order.createdAt)}
            {source ? ` from ${source}` : ""}
          </span>
          <ChannelBadge channel={order.channel} />
        </div>
      </header>

      {order.cancelledAt ? (
        <div className="rounded-xl border border-error/25 bg-error-surface px-4 py-3 text-sm text-error">
          Cancelled on {formatDateTime(order.cancelledAt)}
          {order.cancelReason ? ` · Reason: ${humanizeStatus(order.cancelReason)}` : ""}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <LineItemsCard order={order} />
          <ShipmentsCard order={order} />
          <PaymentCard order={order} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Notes" />
            <CardBody className="text-sm">
              {order.note ? (
                <p className="whitespace-pre-wrap text-text-secondary">{order.note}</p>
              ) : (
                <p className="text-text-muted">No notes from customer</p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Customer" />
            <CardBody className="space-y-3 text-sm">
              <div className="flex items-center gap-2.5">
                <Avatar
                  name={order.customerName}
                  size="sm"
                  className="bg-surface-hover font-medium text-text-secondary"
                />
                <p className="font-medium text-text">{order.customerName}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">
                  Contact information
                </p>
                <p className="mt-1 break-all text-text-secondary">
                  {order.customerEmail || "No email provided"}
                </p>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Tags" />
            <CardBody className="text-sm">
              {order.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {order.tags.map((tag) => (
                    <Badge key={tag} variant="neutral">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-text-muted">No tags</p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function LineItemsCard({ order }: { order: OrderDetail }) {
  const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Card>
      <CardHeader title={`Items (${itemCount})`} />
      <ul className="divide-y divide-border-muted">
        {order.items.map((item) => {
          const lineTotal = item.unitPrice * item.quantity - item.totalDiscount;
          return (
            <li key={item.id} className="flex gap-4 px-6 py-4">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-14 w-14 shrink-0 rounded-lg border border-border object-cover"
                />
              ) : (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-muted">
                  <Package className="h-5 w-5 text-text-subtle" aria-hidden />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="font-medium text-text">{item.title}</p>
                {item.variantTitle ? (
                  <p className="text-sm text-text-muted">{item.variantTitle}</p>
                ) : null}
                {item.sku ? (
                  <p className="text-xs text-text-subtle">SKU: {item.sku}</p>
                ) : null}
              </div>
              <div className="shrink-0 text-right text-sm">
                <p className="text-text-secondary">
                  {formatCurrency(item.unitPrice, order.currency)} × {item.quantity}
                </p>
                {item.totalDiscount > 0 ? (
                  <p className="text-xs text-text-muted">
                    −{formatCurrency(item.totalDiscount, order.currency)} discount
                  </p>
                ) : null}
                <p className="mt-1 font-medium text-text">
                  {formatCurrency(lineTotal, order.currency)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function ShipmentsCard({ order }: { order: OrderDetail }) {
  return (
    <Card>
      <CardHeader title="Fulfillment" />
      {order.shipments.length === 0 ? (
        <CardBody className="text-sm text-text-muted">No fulfillments yet</CardBody>
      ) : (
        <ul className="divide-y divide-border-muted">
          {order.shipments.map((shipment) => (
            <li key={shipment.id} className="flex gap-4 px-6 py-4 text-sm">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-muted">
                <Truck className="h-4 w-4 text-text-muted" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-text">{shipment.name || "Shipment"}</p>
                  <Badge variant={shipmentVariant[shipment.status] ?? "neutral"} dot>
                    {humanizeStatus(shipment.displayStatus || shipment.status)}
                  </Badge>
                </div>
                {shipment.carrier || shipment.trackingNumber ? (
                  <p className="text-text-secondary">
                    {shipment.carrier}
                    {shipment.carrier && shipment.trackingNumber ? " · " : ""}
                    {shipment.trackingUrl && shipment.trackingNumber ? (
                      <a
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                      >
                        {shipment.trackingNumber}
                        <ExternalLink className="h-3 w-3" aria-hidden />
                      </a>
                    ) : (
                      shipment.trackingNumber
                    )}
                  </p>
                ) : (
                  <p className="text-text-muted">No tracking information</p>
                )}
                <p className="text-xs text-text-muted">
                  {shipment.shippedAt ? `Shipped ${formatDateTime(shipment.shippedAt)}` : null}
                  {shipment.deliveredAt
                    ? ` · Delivered ${formatDateTime(shipment.deliveredAt)}`
                    : null}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PaymentCard({ order }: { order: OrderDetail }) {
  const money = (amount: number) => formatCurrency(amount, order.currency);
  const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const shippingTitle = order.shippingLines.map((line) => line.title).join(", ");

  return (
    <Card>
      <CardHeader
        title="Payment"
        action={
          order.financialStatus ? (
            <Badge variant={paymentVariant[order.financialStatus] ?? "neutral"} dot>
              {humanizeStatus(order.financialStatus)}
            </Badge>
          ) : undefined
        }
      />
      <CardBody>
        <dl className="space-y-2.5 text-sm">
          <SummaryRow
            label="Subtotal"
            detail={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
            value={money(order.subtotal)}
          />
          {order.totalDiscounts > 0 ? (
            <SummaryRow
              label="Discount"
              detail={order.discountCodes.join(", ")}
              value={`−${money(order.totalDiscounts)}`}
            />
          ) : null}
          <SummaryRow label="Shipping" detail={shippingTitle} value={money(order.totalShipping)} />
          <SummaryRow label="Taxes" value={money(order.totalTax)} />
          <div className="border-t border-border-muted pt-2.5">
            <SummaryRow label="Total" value={money(order.total)} strong />
          </div>
          {order.totalRefunded > 0 ? (
            <>
              <SummaryRow label="Refunded" value={`−${money(order.totalRefunded)}`} />
              <SummaryRow
                label="Net payment"
                value={money(order.total - order.totalRefunded)}
                strong
              />
            </>
          ) : null}
        </dl>
      </CardBody>
    </Card>
  );
}

function SummaryRow({
  label,
  detail,
  value,
  strong = false,
}: {
  label: string;
  detail?: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-baseline gap-3">
      <dt className={strong ? "font-semibold text-text" : "text-text-secondary"}>{label}</dt>
      <dd className="truncate text-text-muted">{detail}</dd>
      <dd className={strong ? "font-semibold text-text" : "text-text"}>{value}</dd>
    </div>
  );
}

function OrderDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64 rounded-lg" />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Skeleton className="h-56 rounded-2xl" />
          <Skeleton className="h-40 rounded-2xl" />
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    </div>
  );
}
